import { useEffect, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { computeVentNumerics, parseAgeYears } from '../../engine/ventEngine';
import VentWaveform from './VentWaveform';
import VentLoop from './VentLoop';
import AlarmBar from '../monitor/AlarmBar';
import styles from './VentilatorScreen.module.css';

const MENU = ['Audio Pause', 'Alarm Setup', 'Auto Limits', 'System Setup',
              'Next Page', 'Trends', 'Spirometry', 'Procedures'];

// Agent-specific colours (matching ISO vaporizer cap convention):
// sevoflurane yellow, desflurane blue, isoflurane purple.
const AGENT_COLORS = { sevo: '#F2E63D', des: '#0090FF', iso: '#CF9FFF', none: '#8aa' };

/**
 * GE Aisys CS2-style anaesthesia ventilator screen.
 *
 * Layout (matching the reference):
 *   ┌ left: Paw gauge + fresh-gas bars ┬ centre: Paw/Flow/CO₂ waves + loop +
 *   sub-numerics ┬ right: Paw/Flow/CO₂ digit groups ┬ menu chrome ┐
 *   └────────────────── bottom: Fresh-gas + Ventilator settings bar ─────────┘
 */
export default function VentilatorScreen() {
  const rawVentSettings = useSimStore((s) => s.ventSettings);
  const etco2 = useSimStore((s) => s.vitals.EtCO2?.current ?? 0);
  const alarms = useSimStore((s) => s.alarms);
  const patientAge = useSimStore((s) => s.patientAge);
  const intubated = useSimStore((s) => s.intubated);

  // Not intubated → STANDBY: the machine is not delivering positive-pressure
  // ventilation or volatile agent to a secured airway. Zero out the agent so no
  // MAC/agent% is implied, and flatten the airway waveforms. Opening the vent
  // screen alone must not suggest the patient is anaesthetised & tubed.
  const ventSettings = intubated
    ? rawVentSettings
    : { ...rawVentSettings, agent: 'none', agentPct: 0, n2o: 0,
        ppeak: 0, pplat: 0, peep: 0, vte: 0, rr: 0, fio2: 0, fico2: 0 };
  const n = computeVentNumerics(ventSettings, parseAgeYears(patientAge));
  const agentColor = AGENT_COLORS[ventSettings.agent] ?? '#F2E63D';

  const [clock, setClock] = useState(timeStr);
  useEffect(() => {
    const id = setInterval(() => setClock(timeStr()), 1000);
    return () => clearInterval(id);
  }, []);

  // No secured airway → no reliable end-tidal CO₂ sample.
  const etCO2 = intubated ? Math.round(etco2) : '--';

  return (
    <div className={styles.screen}>
      <AlarmBar alarms={alarms} />

      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div className={styles.topbar}>
        {!intubated && (
          <span className={styles.standbyBadge}>
            STANDBY — patient not intubated · no agent delivered
          </span>
        )}
        <span className={styles.clock}>{clock}</span>
      </div>

      <div className={styles.main}>
       {/* Everything except the full-height menu column stacks here: the
           left/centre content row on top, then the full-width settings bar. */}
       <div className={styles.stack}>
        <div className={styles.midRow}>
        {/* ── Left: gauge + fresh-gas bars ───────────────────────────── */}
        <div className={styles.leftCol}>
          <PawGauge value={n.ppeak} />
          <div className={styles.barsRow}>
            <FlowBar label="Total Flow" unit="l/min" max={15} value={n.totalFlow} ticks={[15, 9, 3, 2, 1]} fill="#5DD55D" />
            <FlowBar label="Agent" unit="ml/h" max={300} value={n.agentPct * 30} ticks={[200, 20]} fill={agentColor} />
          </div>
          <div className={styles.leftStats}>
            <Stat label="Total Flow" value={n.totalFlow.toFixed(2)} />
            <Stat label={n.agentLabel} value={n.agentPct.toFixed(0)} accent={agentColor} />
          </div>
        </div>

        {/* ── Centre column: [waves | numerics] top, panels, settings ─── */}
        <div className={styles.contentCol}>
          <div className={styles.topSection}>
            <div className={styles.waves}>
              <WaveRow label="Paw" unit="cmH₂O" color="#F2E63D" scale={['20', '10', '0']} overlay={intubated ? n.mode : 'STANDBY'}>
                <VentWaveform channel="paw" intubated={intubated} />
              </WaveRow>
              <WaveRow label="Flow" unit="l/min" color="#5DD55D" scale={['60', '0', '-60']}>
                <VentWaveform channel="flow" intubated={intubated} />
              </WaveRow>
              <WaveRow label="CO₂" unit="mmHg" color="#FFFFFF" scale={['40', '0']}>
                <VentWaveform channel="co2" intubated={intubated} />
              </WaveRow>
            </div>

            {/* Big digit groups — one per waveform row, aligned to the waves. */}
            <div className={styles.rightNum}>
              <NumGroup title="Paw cmH₂O" limit="⌐40" color="#F2E63D">
                <Dominant
                  big={{ label: 'Ppeak', value: n.ppeak }}
                  side={[{ label: 'Pmean', value: n.pmean }, { label: 'PEEP', value: n.peep }]}
                />
              </NumGroup>
              <NumGroup title="Flow l/min" limit="⌐1000" color="#5DD55D">
                <Dominant
                  big={{ label: 'TVexp ml', value: n.vt }}
                  side={[{ label: 'MV', value: n.mv }, { label: 'MVspont', value: '---' }]}
                />
              </NumGroup>
              <NumGroup title="CO₂ mmHg" limit={`${n.fico2} ⌐60`} color="#FFFFFF">
                <Dominant
                  big={{ label: 'Et', value: etCO2 }}
                  side={[{ label: 'Fi', value: n.fico2 }]}
                />
              </NumGroup>
            </div>
          </div>

          {/* Sub-numeric panels — span the full content width. */}
          <div className={styles.subRow}>
            <div className={styles.loopBox}>
              <span className={styles.loopAxisY}>Vol ml</span>
              <VentLoop />
              <span className={styles.loopAxisX}>Paw cmH₂O</span>
            </div>
            <div className={styles.subPanel}>
              <div className={styles.subHead}>Resp</div>
              <SubVal label="RR /min" value={n.rr} />
              <SubVal label="Compl ml/cmH₂O" value={n.compliance} accent="#F2E63D" />
            </div>
            <div className={styles.subPanel}>
              <div className={styles.subHead}>Gases %</div>
              <div className={styles.etfiHead}><span>Et</span><span>Fi</span></div>
              <SubEtFi label="O₂" et={n.fio2} fi={n.fio2} accent="#5DD55D" />
              <SubEtFi label="N₂O" et={n.etN2o} fi={n.n2o} accent="#29B6F6" />
            </div>
            <div className={styles.subPanel}>
              <div className={styles.subHead}>Agent %</div>
              <div className={styles.etfiHead}><span>Et</span><span>Fi</span></div>
              <SubEtFi label={n.agentLabel} et={n.agentPct.toFixed(1)} fi={(n.agentPct * 1.06).toFixed(1)} accent={agentColor} />
              <SubVal label={`MAC ${n.macAge} y`} value={n.mac.toFixed(1)} accent="#FFFFFF" />
            </div>
          </div>
        </div>{/* /contentCol */}
        </div>{/* /midRow */}

        {/* Bottom settings bar — full width (under left + centre) to the menu. */}
        <div className={styles.bottomBar}>
            <div className={styles.etControl}>
              <span className={styles.barTitle}>Et Control: O₂ + {n.n2o > 0 ? 'N₂O' : 'Air'} + AA</span>
              <div className={styles.setRow}>
                <SetField label="Et O₂" value={n.fio2} unit="Target %" />
                {n.n2o > 0 && <SetField label="N₂O" value={n.n2o} unit="%" />}
                <SetField label="Min Flow" value={n.totalFlow.toFixed(2)} unit="l/min" />
                <SetField label={`Et ${n.agentLabel}`} value={n.agentPct.toFixed(1)} unit="Target %" />
                <div className={styles.etBtns}>
                  <button className={styles.etBtn}>Et Control</button>
                  <button className={styles.etBtn}>Gas Setup</button>
                </div>
              </div>
            </div>
            <div className={styles.ventOn}>
              <span className={styles.barTitle}>
                {intubated ? `Ventilator On: ${modeDescription(n.mode)}` : 'Standby · Not intubated'}
              </span>
              <div className={styles.setRow}>
                <SetField label="Mode" value={n.mode} highlight />
                <SetField label="TV" value={n.vt} unit="ml" />
                <SetField label="RR" value={n.rr} unit="/min" />
                <SetField label="I:E" value={n.ie} />
                <SetField label="PEEP" value={n.peep} unit="cmH₂O" />
                <button className={styles.moreBtn}>More<br />Settings</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Menu chrome (non-interactive), full height ─────────────── */}
        <div className={styles.menuCol}>
          <button className={`${styles.menuBtn} ${styles.menuBtnAlarm}`}>Audio Pause</button>
          {MENU.slice(1).map((m) => (
            <button key={m} className={styles.menuBtn}>{m}</button>
          ))}
          <button className={`${styles.menuBtn} ${styles.menuBtnStart}`}>
            <span>⏱ Start</span><span className={styles.menuSub}>00:00:00</span>
          </button>
          <button className={`${styles.menuBtn} ${styles.menuBtnEnd}`}>End Case</button>
        </div>
      </div>{/* /main */}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
function timeStr() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function modeDescription(mode) {
  return ({
    VCV: 'Volume Control',
    PCV: 'Pressure Control',
    PSV: 'Pressure Support',
  })[mode] ?? mode;
}

function WaveRow({ label, unit, color, scale, overlay, children }) {
  return (
    <div className={styles.waveRow}>
      <div className={styles.waveCanvas}>
        {/* Label overlaid at the top-left corner; scale on the left axis. */}
        <span className={styles.waveTag}>
          <span className={styles.waveName} style={{ color }}>{label}</span>
          <span className={styles.waveUnit}>{unit}</span>
        </span>
        <div className={styles.waveScale}>
          {scale.map((s) => <span key={s}>{s}</span>)}
        </div>
        {overlay && <span className={styles.modeOverlay}>{overlay}</span>}
        {children}
      </div>
    </div>
  );
}

function PawGauge({ value }) {
  // -20..100 dial across a 240° arc; needle at `value`
  const min = -20, max = 100;
  const frac = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const a0 = -210, a1 = 30;                 // degrees
  const ang = (a0 + frac * (a1 - a0)) * Math.PI / 180;
  const cx = 50, cy = 52, r = 34;
  const nx = cx + Math.cos(ang) * r, ny = cy + Math.sin(ang) * r;
  return (
    <div className={styles.gauge}>
      <svg viewBox="0 0 100 100" className={styles.gaugeSvg}>
        <path d={arc(cx, cy, r + 4, a0, a1)} fill="none" stroke="#2a3540" strokeWidth="7" />
        <path d={arc(cx, cy, r + 4, a0, a0 + frac * (a1 - a0))} fill="none" stroke="#F2E63D" strokeWidth="7" />
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#F2E63D" strokeWidth="2.5" />
        <circle cx={cx} cy={cy} r="3" fill="#F2E63D" />
        {['0', '20', '40', '60', '80', '100'].map((t, i) => {
          const f = i / 5;
          const ta = (a0 + f * (a1 - a0)) * Math.PI / 180;
          return <text key={t} x={cx + Math.cos(ta) * (r + 12)} y={cy + Math.sin(ta) * (r + 12)}
            fill="#8aa" fontSize="6" textAnchor="middle" dominantBaseline="middle">{t}</text>;
        })}
      </svg>
      <span className={styles.gaugeLabel}>Paw<br />cmH₂O</span>
    </div>
  );
}

function arc(cx, cy, r, a0, a1) {
  const p0 = polar(cx, cy, r, a0), p1 = polar(cx, cy, r, a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
}
function polar(cx, cy, r, deg) {
  const a = deg * Math.PI / 180;
  return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
}

function FlowBar({ label, unit, max, value, ticks, fill }) {
  const frac = Math.max(0, Math.min(1, value / max));
  return (
    <div className={styles.flowBar}>
      <span className={styles.barLabel}>{label}</span>
      <span className={styles.barUnit}>{unit}</span>
      <div className={styles.barTrack}>
        <div className={styles.barFill} style={{ height: `${frac * 100}%`, background: fill }} />
        {ticks.map((t) => (
          <span key={t} className={styles.barTick} style={{ bottom: `${(t / max) * 100}%` }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue} style={accent ? { color: accent } : undefined}>{value}</span>
    </div>
  );
}

function NumGroup({ title, limit, color, children }) {
  return (
    <div className={styles.numGroup}>
      <div className={styles.numGroupHead}>
        <span style={{ color }}>{title}</span>
        <span className={styles.numLimit}>{limit}</span>
      </div>
      <div className={styles.numGroupBody} style={{ color }}>{children}</div>
    </div>
  );
}

function BigPair({ a, b }) {
  return (
    <div className={styles.bigPair}>
      <Big label={a.label} value={a.value} />
      <Big label={b.label} value={b.value} />
    </div>
  );
}

// One dominant big number on the left, with 1–2 smaller values stacked to the
// right — the GE Aisys parameter-box style (e.g. Ppeak big, Pmean/PEEP small).
function Dominant({ big, side }) {
  return (
    <div className={styles.dominant}>
      <Big label={big.label} value={big.value} />
      <div className={styles.dominantSide}>
        {side.map((s) => <Big key={s.label} label={s.label} value={s.value} small />)}
      </div>
    </div>
  );
}

function Big({ label, value, small }) {
  return (
    <div className={styles.big}>
      <span className={styles.bigLabel}>{label}</span>
      <span className={small ? styles.bigValSmall : styles.bigVal}>{value}</span>
    </div>
  );
}

function SubVal({ label, value, accent }) {
  return (
    <div className={styles.subVal}>
      <span className={styles.subLabel}>{label}</span>
      <span className={styles.subNum} style={accent ? { color: accent } : undefined}>{value}</span>
    </div>
  );
}

function SubEtFi({ label, et, fi, accent }) {
  return (
    <div className={styles.subEtFi}>
      <span className={styles.subLabel}>{label}</span>
      <span className={styles.subNum} style={{ color: accent }}>{et}</span>
      <span className={styles.subNum} style={{ color: accent }}>{fi}</span>
    </div>
  );
}

function SetField({ label, value, unit, highlight }) {
  return (
    <div className={`${styles.setField} ${highlight ? styles.setFieldHi : ''}`}>
      <span className={styles.setLabel}>{label}</span>
      <span className={styles.setValue}>{value}</span>
      {unit && <span className={styles.setUnit}>{unit}</span>}
    </div>
  );
}
